import { api } from "jiffy-api";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { useCallback } from "react";
import { useIsSample } from "@design-lab/shared/lib/config";

type PrintMode = "print_default" | "print_front_back" | "print_sample";

/**
 * Hook to fetch price data for a single product
 */
export function useProductPriceData(productId?: string, printMode: PrintMode = "print_default") {
  const isSample = useIsSample();
  // Override printMode if in sample mode
  const finalPrintMode: PrintMode = isSample ? "print_sample" : printMode;

  return useQuery({
    enabled: !!productId,
    queryKey: productId ? getProductPriceQueryKey(productId, finalPrintMode) : [],
    queryFn: () => {
      if (!productId) return undefined;
      return fetchProductPriceData(productId, finalPrintMode);
    }
  });
}

/**
 * Hook to fetch price data for multiple products
 */
export function useMultipleProductPriceData(
  productIds: string[],
  printMode: PrintMode = "print_default"
) {
  const isSample = useIsSample();
  // Override printMode if in sample mode
  const finalPrintMode: PrintMode = isSample ? "print_sample" : printMode;

  return useQueries({
    queries: productIds.map(productId => ({
      queryKey: getProductPriceQueryKey(productId, finalPrintMode),
      queryFn: () => fetchProductPriceData(productId, finalPrintMode),
      staleTime: 5 * 60 * 1000
    }))
  });
}

/**
 * Hook to warm cache for a single product price data
 */
export function useWarmProductPriceCache() {
  const queryClient = useQueryClient();
  const isSample = useIsSample();

  return useCallback(
    (productId: string, printMode: PrintMode = "print_default") => {
      const finalPrintMode: PrintMode = isSample ? "print_sample" : printMode;
      return queryClient.prefetchQuery({
        queryKey: getProductPriceQueryKey(productId, finalPrintMode),
        queryFn: () => fetchProductPriceData(productId, finalPrintMode)
      });
    },
    [queryClient, isSample]
  );
}

function getProductPriceQueryKey(productId: string, printMode: PrintMode) {
  return ["productPrice", productId, printMode];
}

async function fetchProductPriceData(productId: string, printMode: PrintMode) {
  return api.product.cp({ productId, printMode }).then(objNamesToCamelCase);
}
